(() => {
  if (customElements.get('ft-personalisation')) return;
  const lines = (text, max) => text.split('\n').map(line => line.trim()).filter(Boolean).slice(0, max);
  const savedChoices = new Map();
  class Personalisation extends HTMLElement {
    debug(step, details) {
      if (new URLSearchParams(location.search).get('ftdebug') !== '1') return;
      this.debugStates ||= new Map();
      const signature = JSON.stringify(details);
      if (this.debugStates.get(step) === signature) return;
      this.debugStates.set(step,signature); console.info('[ft-personalisation]',step,details);
    }
    connectedCallback() {
      if (this.controller) return;
      if (!this.querySelector('#ft-personalisation') || !this.querySelector('[data-fields]') || !this.querySelector('[data-preview-fallback]')) {
        this.debug('1-json',{ok:false,reason:'Waiting for product section markup'}); return;
      }
      this.controller = new AbortController();
      const signal = this.controller.signal;
      const read = name => JSON.parse(this.querySelector(name === 'config' ? '#ft-personalisation' : name === 'variants' ? '#ft-variants' : `[data-${name}]`).textContent);
      try { this.config = read('config'); }
      catch (error) { this.debug('1-json',{ok:false,reason:error.message}); this.controller = null; return; }
      if (!this.config || this.config.enabled !== true || !this.config.fields?.length) { this.hidden = true; this.debug('1-json',{ok:false,reason:'Personalisation disabled or fields empty'}); return; }
      this.debug('1-json',{ok:true,enabled:true,fields:this.config.fields.length});
      this.toggleAttribute('data-compact', this.config.fields.length > 4);
      this.variants = read('variants'); this.options = read('options'); this.initial = read('selected'); this.productImage = read('product-image');
      this.form = this.closest('form');
      if (!this.form) { this.debug('2-section',{ok:false,reason:'Waiting for cart form'}); this.controller = null; return; }
      this.section = document.getElementById(`ProductSection-${this.dataset.sectionId}`) || this.form.parentElement;
      this.fallback = this.querySelector('[data-preview-fallback]'); this.fallbackCanvas = this.fallback.querySelector('canvas');
      this.photos = new Map(); this.fontsReady = false;
      this.querySelector('[data-fields]').replaceChildren();
      this.fields = this.config.fields.map((field, index) => this.build(field, index));
      this.choiceKey = `${this.dataset.productId || location.pathname.split('/products/')[1]?.split('/')[0]}:${this.dataset.sectionId}`;
      const saved = savedChoices.get(this.choiceKey);
      for (const state of this.fields) {
        const choice = saved?.find(choice=>choice.key===state.field.key);
        if (!choice) continue;
        state.input.value = choice.text;
        state.font = state.fonts.find(font=>font.id===choice.font) || state.font;
        if (state.picker?.tagName === 'SELECT') state.picker.value = state.font.id;
      }
      this.debug('2-section',{ok:true,section:this.dataset.sectionId,restored:!!saved});
      this.payload = document.createElement('input'); this.payload.type = 'hidden';
      this.payload.name = `properties[${this.config.property || '_personalisation'}]`; this.append(this.payload);
      this.section.addEventListener('change', () => this.schedule(), { signal });
      this.section.addEventListener('load', () => this.schedule(), { capture: true, signal });
      document.addEventListener('submit', event => { if (event.target === this.form) this.guard(event); }, { capture: true, signal });
      document.addEventListener('click', event => {
        const control = event.target.closest('button,input[type=submit],.shopify-payment-button');
        if (control && (control.form === this.form || this.form.contains(control)) && (control.matches('[name=add],[type=submit],.shopify-payment-button') || control.closest('.shopify-payment-button'))) this.guard(event);
      }, { capture: true, signal });
      this.observer = new MutationObserver(() => this.schedule());
      this.observer.observe(this.section, { subtree: true, childList: true, attributes: true, attributeFilter: ['class','style','src','srcset','hidden','data-media-id','data-variant-id','value'] });
      this.resize = new ResizeObserver(() => this.schedule()); this.resize.observe(this.section);
      window.addEventListener('resize', () => this.schedule(), { signal });
      this.watchResolution();
      window.addEventListener('popstate', () => this.schedule(), { signal });
      for (const name of ['variant:change','variant-change','variant:changed']) this.section.addEventListener(name, () => this.schedule(), { signal });
      this.variantTimer = setInterval(() => {
        const stamp = `${this.form.querySelector('[name=id]')?.value}|${location.search}|${this.section.querySelector('variant-selects,variant-radios')?.dataset.variantId || ''}`;
        if (stamp !== this.variantStamp) { this.variantStamp = stamp; this.schedule(); }
      }, 150);
      document.fonts?.addEventListener('loadingdone', () => this.schedule(), { signal });
      this.loadFonts(signal); this.update();
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller = null; this.observer?.disconnect(); this.resize?.disconnect(); clearInterval(this.variantTimer);
      cancelAnimationFrame(this.frame); this.frame = 0; this.canvas?.remove(); this.canvas = null; this.image = null; this.payload?.remove();
      this.lastFormId = undefined; this.lastUrlId = undefined;
    }
    watchResolution() {
      const signal = this.controller.signal;
      matchMedia(`(resolution: ${window.devicePixelRatio || 1}dppx)`).addEventListener('change', () => {
        if (!signal.aborted) { this.schedule(); this.watchResolution(); }
      }, { once:true, signal });
    }
    async loadFonts(signal) {
      if (this.config.stylesheet) {
        let link = [...document.querySelectorAll('link[rel=stylesheet]')].find(link => link.href === this.config.stylesheet);
        const fresh = !link;
        if (!link) { link = document.createElement('link'); link.rel = 'stylesheet'; link.href = this.config.stylesheet; }
        const loaded = new Promise(resolve => {
          if (link.sheet) return resolve();
          link.addEventListener('load', resolve, { once:true, signal }); link.addEventListener('error', resolve, { once:true, signal });
          setTimeout(resolve, 4000);
        });
        if (fresh) document.head.append(link); await loaded;
      }
      await Promise.allSettled(this.config.fonts.map(font => document.fonts.load(`${font.weight || 400} 40px ${JSON.stringify(font.family)}`)));
      if (signal.aborted) return;
      this.fontsReady = true; this.schedule();
      this.debug('7-fonts',{ok:true,fonts:this.config.fonts.map(font=>font.id)});
      document.fonts.ready.then(() => { if (!signal.aborted) this.schedule(); });
    }
    build(field, index) {
      const group = document.createElement('div'); group.className = 'ft-personal-field';
      const id = `ft-personal-${this.dataset.sectionId}-${index}`;
      const label = document.createElement('label'); label.htmlFor = id; label.textContent = field.label;
      const input = document.createElement(field.maxLines > 1 ? 'textarea' : 'input');
      if (field.maxLines > 1) input.rows = field.maxLines;
      else { input.type = 'text'; input.maxLength = field.maxChars; }
      input.id = id; input.placeholder = field.placeholder || ''; input.setAttribute('aria-required', String(!!field.required));
      input.setAttribute('aria-describedby', `${id}-count ${id}-error ${id}-warning`);
      const counter = document.createElement('small'); counter.id = `${id}-count`;
      const error = document.createElement('p'); error.id = `${id}-error`; error.dataset.error = ''; error.setAttribute('aria-live', 'polite');
      const warning = document.createElement('p'); warning.id = `${id}-warning`; warning.hidden = true;
      warning.textContent = "That's a lot of text for this space, so it'll print small. Try something shorter.";
      const fonts = field.fonts.map(id => this.config.fonts.find(font => font.id === id)).filter(Boolean);
      const state = { field, input, counter, error, warning, fonts, font: fonts.find(font => font.id === field.defaultFont) || fonts[0] };
      group.append(label, input, counter);
      if (fonts.length > 1) {
        const useSelect = fonts.length > 6 || this.config.fields.length > 4;
        const picker = document.createElement(useSelect ? 'select' : 'fieldset');
        if (useSelect) {
          const fontLabel = document.createElement('label'); fontLabel.htmlFor = `${id}-font`; fontLabel.textContent = `${field.label} font`;
          picker.id = `${id}-font`; group.append(fontLabel);
          fonts.forEach(font => { const option = new Option(font.name, font.id); option.style.fontFamily = font.family; picker.add(option); });
          picker.value = state.font.id;
          picker.addEventListener('change', () => { state.font = fonts.find(font => font.id === picker.value); this.update(); });
        } else {
          const legend = document.createElement('legend'); legend.textContent = `${field.label} font`; picker.append(legend);
          fonts.forEach(font => {
            const button = document.createElement('button'); button.type = 'button'; button.textContent = font.name; button.style.fontFamily = font.family;
            button.dataset.font = font.id;
            button.addEventListener('click', () => { state.font = font; this.update(); }); picker.append(button);
          });
        }
        group.append(picker); state.picker = picker;
      }
      state.properties = [field.label, `${field.label} font`].map(name => {
        const hidden = document.createElement('input'); hidden.type = 'hidden'; hidden.name = `properties[${name}]`; group.append(hidden); return hidden;
      });
      group.append(error, warning); this.querySelector('[data-fields]').append(group);
      input.addEventListener('input', () => this.update(true));
      return state;
    }
    guard(event) {
      this.update();
      let first;
      this.fields.forEach(state => {
        const invalid = state.field.required && !state.text;
        state.error.textContent = invalid ? `Enter the ${state.field.label.toLowerCase()}.` : '';
        state.input.setAttribute('aria-invalid', String(!!invalid));
        if (invalid && !first) first = state.input;
      });
      if (first) { event.preventDefault(); event.stopImmediatePropagation(); first.scrollIntoView({ block: 'center', behavior: 'smooth' }); first.focus({ preventScroll: true }); }
    }
    schedule() {
      if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = 0; this.draw(); });
    }
    update(typing = false) {
      const payload = [];
      this.fields.forEach(state => {
        let value = state.input.value.replace(/\r\n?/g, '\n');
        if (state.field.textCase === 'upper') value = value.toUpperCase();
        let remaining = state.field.maxChars;
        value = value.split('\n').slice(0, state.field.maxLines || 1).map(line => {
          const part = line.slice(0, Math.max(0, remaining)); remaining -= part.length; return part;
        }).join('\n');
        if (value !== state.input.value) state.input.value = value;
        state.text = lines(value, state.field.maxLines || 1).join('\n');
        const count = `${value.replace(/\n/g, '').length} / ${state.field.maxChars}`;
        if (state.counter.textContent !== count) state.counter.textContent = count;
        if (state.text && state.error.textContent) { state.error.textContent = ''; state.input.setAttribute('aria-invalid', 'false'); }
        state.properties[0].value = state.text; state.properties[0].disabled = !state.text;
        state.properties[1].value = state.font.name; state.properties[1].disabled = !state.text || state.fonts.length < 2;
        state.picker?.querySelectorAll('button').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.font === state.font.id)));
        if (state.text) payload.push({ key: state.field.key, label: state.field.label, text: state.text, font: state.font.id });
      });
      this.payload.value = JSON.stringify({ fields: payload }); this.payload.disabled = !payload.length;
      if (this.choiceKey) savedChoices.set(this.choiceKey,this.fields.map(state=>({key:state.field.key,text:state.input.value,font:state.font.id})));
      this.schedule(typing);
    }
    selected() {
      const picker = this.section.querySelector('variant-selects,variant-radios');
      const formId = this.form.querySelector('[name=id]')?.value || '';
      const urlId = new URLSearchParams(location.search).get('variant') || '';
      if (this.lastFormId === undefined) this.selectedId = formId || urlId || String(this.initial?.id || '');
      else if (urlId !== this.lastUrlId && urlId) this.selectedId = urlId;
      else if (formId !== this.lastFormId) this.selectedId = formId;
      this.lastFormId = formId; this.lastUrlId = urlId;
      const id = this.selectedId;
      const stored = this.variants.find(item => String(item.id) === id) || (String(this.initial?.id) === id ? this.initial : null);
      const current = String(picker?.currentVariant?.id) === id ? picker.currentVariant : null;
      const variant = stored ? { ...current, ...stored } : current ? { ...current, featured_image: current.featured_image || (picker.dataset.imageUrl ? { src: picker.dataset.imageUrl } : null) } : null;
      const index = this.options.findIndex(name => (name.name || name).toLowerCase() === 'colour');
      const colourIndex = index < 0 ? 0 : index;
      const colourControl = picker?.querySelector(`.pvOpt${colourIndex} select,.pvOpt${colourIndex} input:checked`) || picker?.querySelectorAll('[data-ft-option]')[colourIndex];
      const rawColour = variant?.options?.[colourIndex] ?? variant?.[`option${colourIndex + 1}`] ?? colourControl?.value;
      const trimmed = String(rawColour ?? '').trim();
      const keys = Object.keys(this.config.colours || {});
      const colour = keys.find(key=>key.trim()===trimmed) ?? keys.find(key=>key.trim().toLowerCase()===trimmed.toLowerCase()) ?? trimmed;
      this.debug('3-variant',{ok:!!variant&&keys.includes(colour),variant:variant?.id,rawColour,colour,reason:!variant?'Selected variant not found':!keys.includes(colour)?'No matching colour metadata':undefined});
      return { variant, colour };
    }
    optionValue(name, variant) {
      const index = this.options.findIndex(option => String(option.name || option).trim().toLowerCase() === String(name || '').trim().toLowerCase());
      if (index < 0) return undefined;
      const picker = this.section.querySelector('variant-selects,variant-radios');
      const control = picker?.querySelector(`.pvOpt${index} select,.pvOpt${index} input:checked`) || picker?.querySelectorAll('[data-ft-option]')[index];
      return variant?.options?.[index] ?? variant?.[`option${index + 1}`] ?? control?.value;
    }
    textColour(field, box, variant) {
      const vinyl = this.config.vinyl;
      if (vinyl && field.followsVinyl === true) {
        const value = this.optionValue(vinyl.option, variant);
        if (value !== undefined && Object.prototype.hasOwnProperty.call(vinyl.colours || {}, value) && vinyl.colours[value]) return vinyl.colours[value];
      }
      return box.colour || '#000';
    }
    photoRect(image, width, height) {
      const style = getComputedStyle(image);
      if (!['contain','cover'].includes(style.objectFit)) return { x:0, y:0, width, height };
      const scale = Math[style.objectFit === 'cover' ? 'max' : 'min'](width/image.naturalWidth, height/image.naturalHeight);
      const w = image.naturalWidth*scale, h = image.naturalHeight*scale;
      const tokens = style.objectPosition.split(/\s+/);
      const offset = (token, free) => token === 'left' || token === 'top' ? 0 : token === 'right' || token === 'bottom' ? free : token.endsWith('%') ? free*parseFloat(token)/100 : token === 'center' ? free/2 : parseFloat(token)||0;
      return { x:offset(tokens[0] || '50%',width-w), y:offset(tokens[1] || '50%',height-h), width:w, height:h };
    }
    paint(canvas, width, height, rect, colourData, variant) {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.max(1, Math.round(width*dpr)); canvas.height = Math.max(1, Math.round(height*dpr));
      const ctx = canvas.getContext('2d'); ctx.scale(dpr,dpr);
      ctx.beginPath(); ctx.rect(0,0,width,height); ctx.clip();
      for (const state of this.fields) {
        const box = colourData?.boxes?.[state.field.key];
        if (!state.text || box?.hidden || !['x','y','width','height'].every(key => Number.isFinite(box?.[key])) || box.width <= 0 || box.height <= 0) { this.setHidden(state.warning,true); continue; }
        const textLines = lines(state.text,state.field.maxLines || 1), font = state.font;
        if (!textLines.length) continue;
        const metrics = (font.ascender-font.descender)/font.unitsPerEm*(this.config.lineSpacing || 1);
        if (!(metrics > 0)) continue;
        const x = rect.x+box.x*rect.width, y = rect.y+box.y*rect.height, w = box.width*rect.width, h = box.height*rect.height;
        ctx.font = `${font.weight || 400} 100px ${JSON.stringify(font.family)}`;
        const ideal = h/(textLines.length*metrics);
        const fitted = Math.min(ideal,...textLines.map(line => w/(ctx.measureText(line).width/100 || 1)));
        const size = fitted*.95;
        this.setHidden(state.warning,fitted >= (this.config.minTextScale ?? .3)*ideal);
        ctx.save();
        const clip = colourData?.design?.clip;
        if (clip && ['x','y','width','height'].every(key => Number.isFinite(clip[key])) && clip.width > 0 && clip.height > 0) {
          const cx = rect.x+clip.x*rect.width, cy = rect.y+clip.y*rect.height, cw = clip.width*rect.width, ch = clip.height*rect.height;
          ctx.beginPath();
          if (clip.round) ctx.ellipse(cx+cw/2,cy+ch/2,cw/2,ch/2,0,0,2*Math.PI); else ctx.rect(cx,cy,cw,ch);
          ctx.clip();
        }
        const px = rect.x+(box.pivotX ?? .5)*rect.width, py = rect.y+(box.pivotY ?? .5)*rect.height;
        ctx.translate(px,py); ctx.rotate((box.rotation || 0)*Math.PI/180); ctx.translate(-px,-py);
        ctx.translate(x+w/2,y+h/2); ctx.rotate((state.field.rotation || 0)*Math.PI/180); ctx.translate(-x-w/2,-y-h/2);
        ctx.font = `${font.weight || 400} ${size}px ${JSON.stringify(font.family)}`; ctx.fillStyle = this.textColour(state.field,box,variant); ctx.textBaseline = 'alphabetic';
        ctx.textAlign = ['left','right'].includes(state.field.align) ? state.field.align : 'center';
        const tx = x+(ctx.textAlign === 'left' ? 0 : ctx.textAlign === 'right' ? w : w/2);
        const top = y+(h-textLines.length*metrics*size)/2;
        textLines.forEach((line,index) => ctx.fillText(line,tx,top+index*metrics*size+font.ascender/font.unitsPerEm*size));
        ctx.restore();
      }
    }
    setHidden(element, hidden) { if (element.hidden !== hidden) element.hidden = hidden; }
    draw() {
      if (!this.fields || !this.isConnected) { this.debug('2-section',{ok:false,reason:'Section not ready or detached'}); return; }
      if (!this.fontsReady) { this.debug('7-fonts',{ok:false,reason:'Waiting for configured fonts'}); return; }
      const {variant,colour} = this.selected();
      const colourData = this.config.colours?.[colour];
      const usable = this.fields.filter(state=>state.text&&!colourData?.boxes?.[state.field.key]?.hidden&&['x','y','width','height'].every(key=>Number.isFinite(colourData?.boxes?.[state.field.key]?.[key])));
      this.debug('4-boxes',{ok:usable.length>0,usable:usable.map(state=>state.field.key),reason:usable.length?undefined:this.fields.some(state=>state.text)?'No usable boxes for entered text':'Waiting for customer text'});
      const mediaId = variant?.featured_media?.id;
      const gallery = this.section.querySelector('media-gallery');
      const escaped = CSS.escape(String(mediaId || ''));
      const slides = mediaId ? [...(gallery?.querySelectorAll(`[data-id="media${escaped}"],[data-media-id="${escaped}"],[data-media-id="media${escaped}"]`) || [])].filter(slide=>!slide.closest('.thumbs_nav')) : [];
      const active = gallery?.querySelector('.pr_lg_img .swiper-slide-active,.product__media-list .is-active,[data-main-media] .is-active');
      const activeId = active?.dataset.mediaId || active?.dataset.id;
      const activeMatches = active && (slides.some(slide=>slide===active||slide.contains(active)||active.contains(slide)) || [String(mediaId),`media${mediaId}`].includes(activeId));
      const slide = (activeMatches && active) || slides.find(slide=>slide.getBoundingClientRect().width>0) || slides[0] || (!slides.length && activeMatches && active);
      const image = slide?.querySelector('img');
      if (image && active && !activeMatches) {
        if (this.canvas) this.setHidden(this.canvas,true);
        this.setHidden(this.fallback,true); this.debug('5-image',{ok:false,reason:'Customer is viewing a different gallery slide',mediaId}); return;
      }
      this.debug('5-image',{ok:!!image?.complete&&!!image?.naturalWidth&&image.getBoundingClientRect().width>0,mediaId,found:!!image,loaded:!!image?.complete&&!!image?.naturalWidth,width:image?.getBoundingClientRect().width||0,reason:!image?'Selected media image not found':!image.complete||!image.naturalWidth?'Image still loading':image.getBoundingClientRect().width===0?'Image has zero rendered width':undefined});
      if (image?.complete && image.naturalWidth && image.getBoundingClientRect().width > 0 && getComputedStyle(image).visibility !== 'hidden' && getComputedStyle(slide).display !== 'none') {
        this.setHidden(this.fallback,true);
        if (this.image !== image || !this.canvas?.isConnected || this.canvas.parentElement !== image.parentElement) {
          if (this.image) this.resize.unobserve(this.image);
          this.canvas?.remove(); this.image = image; this.canvas = document.createElement('canvas');
          this.canvas.setAttribute('aria-hidden','true'); this.canvas.dataset.personalisationOverlay = '';
          this.canvas.style.cssText = 'position:absolute;pointer-events:none;z-index:20;';
          const parent = image.parentElement;
          if (getComputedStyle(parent).position === 'static') parent.style.position = 'relative';
          parent.append(this.canvas); this.resize.observe(image);
        }
        const bounds = image.getBoundingClientRect(), parent = image.parentElement, outer = parent.getBoundingClientRect();
        const width = bounds.width, height = bounds.height;
        const values = {left:`${bounds.left-outer.left+parent.scrollLeft-parent.clientLeft}px`,top:`${bounds.top-outer.top+parent.scrollTop-parent.clientTop}px`,width:`${width}px`,height:`${height}px`};
        for (const [key,value] of Object.entries(values)) if (this.canvas.style[key] !== value) this.canvas.style[key] = value;
        this.setHidden(this.canvas,false);
        this.paint(this.canvas,width,height,this.photoRect(image,width,height),colourData,variant);
        this.debug('6-canvas',{ok:width>0&&height>0&&this.canvas.isConnected,width,height,parent:image.parentElement.tagName,zIndex:20,pointerEvents:'none'});
        return;
      }
      if (this.canvas) this.setHidden(this.canvas,true);
      this.drawFallback(variant,colourData);
      this.debug('6-canvas',{ok:!this.fallback.hidden,fallback:true,reason:'Main gallery image unavailable; using fallback'});
    }
    drawFallback(variant,colourData) {
      const featured = variant?.featured_image;
      const source = (typeof featured === 'string' ? featured : featured?.src) || this.productImage;
      if (!source) { this.setHidden(this.fallback,true); return; }
      this.setHidden(this.fallback,false);
      if (!this.photos.has(source)) {
        const image = new Image(); const entry = {image,loaded:false}; this.photos.set(source,entry);
        image.onload = () => { entry.loaded=true; if (this.isConnected) this.schedule(); };
        image.onerror = () => { entry.failed=true; }; image.src = source;
      }
      const entry = this.photos.get(source); if (!entry.loaded) return;
      const side = this.fallbackCanvas.clientWidth || this.clientWidth || 300;
      const scale = Math.min(side/entry.image.naturalWidth,side/entry.image.naturalHeight);
      const width = entry.image.naturalWidth*scale, height = entry.image.naturalHeight*scale;
      const rect = { x:(side-width)/2,y:(side-height)/2,width,height };
      this.paint(this.fallbackCanvas,side,side,rect,colourData,variant);
      // The photo sits behind the already clipped and fitted text.
      const ctx = this.fallbackCanvas.getContext('2d'); ctx.save(); ctx.globalCompositeOperation='destination-over';
      ctx.drawImage(entry.image,rect.x,rect.y,width,height); ctx.restore();
    }
  }
  customElements.define('ft-personalisation',Personalisation);
  const initialise = scope => {
    const roots = scope.matches?.('ft-personalisation') ? [scope] : [...(scope.querySelectorAll?.('ft-personalisation') || [])];
    for (const root of roots) { if (!root.controller) root.connectedCallback(); else root.schedule(); }
  };
  document.addEventListener('shopify:section:load',event=>initialise(event.target));
  new MutationObserver(records=>{
    for (const record of records) for (const node of record.addedNodes) if (node.nodeType===1) {
      const root = node.closest?.('ft-personalisation');
      if (root&&!root.controller) initialise(root); else initialise(node);
    }
  }).observe(document.documentElement,{childList:true,subtree:true});
  initialise(document);
})();
